"use client";

import type { SupportedLanguage } from "../../config/languages";
import type { PersonioJob } from "../../helpers/getPersonioJobs";
import { ActionButton } from "../ActionButton/ActionButton";
import { ArrowDownSmall } from "../icons/ArrowDownSmall";
import { BriefcaseIcon } from "../icons/BriefcaseIcon";
import { PinIcon } from "../icons/PinIcon";
import classNames from "classnames/bind";
import { ChangeEvent, ReactNode, SubmitEvent, useMemo, useState } from "react";
import styles from "./Jobs.module.css";

const cx = classNames.bind(styles);

type JobsTranslations = {
  filtersAriaLabel: string;
  departmentsLabel: string;
  countryLabel: string;
  allDepartments: string;
  allCountries: string;
  searchLabel: string;
  searchPlaceholder: string;
  searchSubmit: string;
  fullTime: string;
  partTime: string;
  applyNow: string;
  noJobsFound: string;
};

const JOBS: Record<SupportedLanguage, JobsTranslations> = {
  en: {
    filtersAriaLabel: "Job filters",
    departmentsLabel: "Departments",
    countryLabel: "Country",
    allDepartments: "All departments",
    allCountries: "All countries",
    searchLabel: "Search by keyword…",
    searchPlaceholder: "Search by keyword…",
    searchSubmit: "Search",
    fullTime: "Full time",
    partTime: "Part time",
    applyNow: "Apply now",
    noJobsFound: "No jobs found.",
  },
  de: {
    filtersAriaLabel: "Stellenfilter",
    departmentsLabel: "Bereiche",
    countryLabel: "Land",
    allDepartments: "Alle Bereiche",
    allCountries: "Alle Länder",
    searchLabel: "Nach Stichwort suchen …",
    searchPlaceholder: "Nach Stichwort suchen …",
    searchSubmit: "Suchen",
    fullTime: "Vollzeit",
    partTime: "Teilzeit",
    applyNow: "Jetzt bewerben",
    noJobsFound: "Keine Stellen gefunden.",
  },
};

interface JobsProps {
  locale: SupportedLanguage;
  /** Language of the hard-coded labels. Defaults to `locale`. */
  copyLocale?: SupportedLanguage;
  jobs: PersonioJob[];
  cta?: {
    text: string;
    href: string;
  };
}

export const Jobs = ({ jobs, cta, locale, copyLocale = locale }: JobsProps) => {
  const jobsCopy = JOBS[copyLocale];
  const [department, setDepartment] = useState("");
  const [country, setCountry] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const departments = useMemo(
    () =>
      getUniqueOptions(
        jobs.map((job) => job.department),
        locale,
      ),
    [jobs, locale],
  );
  const countries = useMemo(
    () =>
      getUniqueOptions(
        jobs.map((job) => job.country),
        locale,
      ),
    [jobs, locale],
  );

  const filteredJobs = useMemo(
    () =>
      jobs.filter((job) => {
        const normalizedSearchQuery = searchQuery.trim().toLowerCase();
        const titleMatches =
          !normalizedSearchQuery ||
          job.name.toLowerCase().includes(normalizedSearchQuery);
        const departmentMatches = !department || job.department === department;
        const countryMatches = !country || job.country === country;

        return titleMatches && departmentMatches && countryMatches;
      }),
    [jobs, searchQuery, country, department],
  );
  const isFiltered = Boolean(department || country || searchQuery.trim());
  const visibleJobs = isFiltered ? filteredJobs : filteredJobs.slice(0, 4);

  const handleSearch = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearchQuery(searchInput);
  };

  return (
    <div className={cx("jobs")}>
      <div className={cx("filters")} aria-label={jobsCopy.filtersAriaLabel}>
        {departments.length > 0 && (
          <Dropdown
            id="job-departments"
            label={jobsCopy.departmentsLabel}
            options={departments}
            value={department}
            allLabel={jobsCopy.allDepartments}
            onChange={setDepartment}
          />
        )}
        {countries.length > 0 && (
          <Dropdown
            id="job-countries"
            label={jobsCopy.countryLabel}
            options={countries}
            value={country}
            allLabel={jobsCopy.allCountries}
            onChange={setCountry}
          />
        )}
        <form className={cx("search")} onSubmit={handleSearch}>
          <label className={cx("search-label")} htmlFor="job-title-search">
            {jobsCopy.searchLabel}
          </label>
          <input
            className={cx("search-input")}
            id="job-title-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={jobsCopy.searchPlaceholder}
          />
          <ActionButton
            className={cx("search-button")}
            type="submit"
            variant="light"
            icon="arrow-small"
          >
            {jobsCopy.searchSubmit}
          </ActionButton>
        </form>
      </div>
      {visibleJobs.length > 0 ? (
        <div className={cx("job-list")}>
          {visibleJobs.map((job) => (
            <a key={job.id} className={cx("job")} href={job.url}>
              <span className={cx("job-title")}>{job.name}</span>
              {job.office || job.schedule ? (
                <span className={cx("job-pills")}>
                  {job.office && (
                    <Pill icon="pin">
                      {job.office}
                      {job.code ? `, ${job.code}` : null}
                    </Pill>
                  )}
                  {job.schedule.map((schedule) => {
                    switch (schedule) {
                      case "full-time":
                        return (
                          <Pill key={schedule} icon="briefcase">
                            {jobsCopy.fullTime}
                          </Pill>
                        );
                      case "part-time":
                        return (
                          <Pill key={schedule} icon="briefcase">
                            {jobsCopy.partTime}
                          </Pill>
                        );
                    }
                  })}
                </span>
              ) : null}
              <ActionButton
                asChild
                className={cx("job-apply-button")}
                variant="sky"
                size="small"
                icon="external"
              >
                <span>{jobsCopy.applyNow}</span>
              </ActionButton>
            </a>
          ))}
        </div>
      ) : (
        <p className={cx("no-jobs-found")}>{jobsCopy.noJobsFound}</p>
      )}
      {cta && !isFiltered ? (
        <div className={cx("cta-button")}>
          <ActionButton
            href={cta.href}
            locale={locale}
            variant="light"
            icon="arrow-small"
          >
            {cta.text}
          </ActionButton>
        </div>
      ) : null}
    </div>
  );
};

interface Dropdown {
  id: string;
  label: string;
  options: string[];
  value: string;
  allLabel: string;
  onChange: (value: string) => void;
}

function Dropdown({ id, label, options, value, allLabel, onChange }: Dropdown) {
  const selectedLabel = value || allLabel;

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  return (
    <div className={cx("filter")}>
      <label className={cx("filter-label")} htmlFor={id}>
        {label}
      </label>
      <select
        className={cx("filter-select")}
        id={id}
        value={value}
        onChange={handleChange}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className={cx("filter-value")} aria-hidden="true">
        {selectedLabel}
      </span>
      <ArrowDownSmall className={cx("filter-arrow")} />
    </div>
  );
}

type Icon = "briefcase" | "pin";

function Pill({ children, icon }: { children: ReactNode; icon?: Icon }) {
  return (
    <span className={cx("pill")}>
      {icon && <Icon icon={icon} />}
      <span className={cx("pill-content")}>{children}</span>
    </span>
  );
}

function Icon({ icon }: { icon: Icon }) {
  switch (icon) {
    case "briefcase":
      return <BriefcaseIcon />;
    case "pin":
      return <PinIcon />;
  }
}

function getUniqueOptions(
  values: Array<string | null | undefined>,
  locale: SupportedLanguage,
) {
  return Array.from(
    new Set(values.filter((value): value is string => !!value)),
  ).sort((a, b) => a.localeCompare(b, locale));
}
